import type React from "react";
import { createContext, useContext, useState, useCallback, useEffect } from "react";
import { Snackbar, Alert } from "@mui/material";
import { toastEmitter } from "../utils/toastEmitter";

interface ToastContextType {
  showToast: (message: string, severity?: "success" | "error" | "info" | "warning") => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

export const ToastProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [message, setMessage] = useState("");
  const [severity, setSeverity] = useState<"success" | "error" | "info" | "warning">("error");

  const showToast = useCallback(
    (message: string, severity: "success" | "error" | "info" | "warning" = "error") => {
      setMessage(message);
      setSeverity(severity);
      setOpen(true);
    },
    [],
  );

  useEffect(() => {
    const unsubscribe = toastEmitter.subscribe(({ message, severity }) => {
      showToast(message, severity);
    });

    return () => {
      unsubscribe();
    };
  }, [showToast]);

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <ToastContext.Provider value={{ showToast }}>
      {children}
      <Snackbar
        open={open}
        autoHideDuration={10000}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert onClose={handleClose} severity={severity} sx={{ width: "100%" }}>
          {message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};

export const useToast = () => {
  const context = useContext(ToastContext);
  if (context === undefined) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
};
